import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Video, Check, ArrowRight, Copy, Sparkles, Building2, CreditCard, Users, Palette } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, hint, required, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs font-bold uppercase tracking-widest text-white/70">
      {label} {required && <span className="text-[#22D3EE]">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-white/45">{hint}</span>}
  </label>
);

const Input = (p) => (
  <input
    {...p}
    className={"h-11 px-4 rounded-xl bg-white/5 border border-white/15 focus:border-[#22D3EE] focus:bg-white/8 text-white outline-none placeholder:text-white/30 transition-colors " + (p.className || "")}
  />
);

const TextArea = (p) => (
  <textarea
    {...p}
    className={"px-4 py-3 rounded-xl bg-white/5 border border-white/15 focus:border-[#22D3EE] focus:bg-white/8 text-white outline-none placeholder:text-white/30 transition-colors " + (p.className || "")}
  />
);

const Section = ({ icon: Icon, title, tint, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tint + "22", color: tint }}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="font-display font-black text-lg text-white">{title}</div>
    </div>
    <div className="grid gap-4">{children}</div>
  </div>
);

export default function OnboardingForm() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    whatsapp: "",
    country_code: "+91",
    timezone: "Asia/Kolkata",
    business_name: "",
    niche: "",
    target_audience: "",
    usp_line: "",
    speaker_bio: "",
    speaker_photo_url: "",
    logo_url: "",
    primary_color: "#0B5CFF",
    accent_color: "#22D3EE",
    instagram: "",
    youtube: "",
    linkedin: "",
    razorpay_key_id: "",
    razorpay_key_secret: "",
    pan_number: "",
    gst_number: "",
    zoom_account_id: "",
    zoom_client_id: "",
    zoom_client_secret: "",
    business_address: "",
    business_pincode: "",
    first_webinar_title: "",
    first_webinar_price: 0,
    accept_terms: true,
  });

  useEffect(() => {
    axios.get(`${API}/api/onboarding/${token}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, full_name: r.data.client_name || "", email: r.data.client_email || "" }));
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/${token}`, form);
      setDone(data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Kuch galat hua — ek baar aur try karo");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050B24] text-white px-6">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-5">
            <Video className="w-6 h-6" />
          </div>
          <h1 className="font-display font-black text-3xl">Invite link is invalid</h1>
          <p className="text-white/60 mt-3">Ye onboarding link expire ho gaya hai ya galat hai. Founder se new link maango.</p>
          <Link to="/" className="text-[#22D3EE] hover:underline mt-6 inline-block">Go to Videoepreneur homepage →</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050B24] text-white px-6 py-16">
        <div className="max-w-xl text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center mb-6 shadow-2xl">
            <Check className="w-8 h-8" strokeWidth={2.5} />
          </div>
          <h1 className="font-display font-black text-4xl leading-tight">Setup complete 🎉</h1>
          <p className="text-white/70 mt-4 text-lg">
            Aapka Videoepreneur workspace ready hai, <strong className="text-white">{form.full_name}</strong>. Ab login karo aur apna pehla webinar create karo.
          </p>
          <a href="/login" data-testid="onboarding-goto-login" className="inline-flex items-center gap-2 mt-8 px-7 py-3 rounded-full bg-white text-[#050B24] font-bold hover:scale-[1.03] transition-transform">
            Log in to workspace <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  if (!invite) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050B24] text-white">Loading invite…</div>;
  }

  return (
    <div className="min-h-screen bg-[#050B24] text-white">
      {/* Header */}
      <header className="border-b border-white/8">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0B5CFF] flex items-center justify-center">
            <Video className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-black leading-none">Videoepreneur</div>
            <div className="text-[10px] uppercase tracking-widest text-white/45 font-bold mt-1">Creator Onboarding</div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-12 pb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#22D3EE]/15 border border-[#22D3EE]/25 text-[#22D3EE] text-[11px] font-bold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Personal invite from Videoepreneur
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight leading-[1.05]">
          Welcome <span className="text-[#22D3EE]">{invite.client_name}</span>.
        </h1>
        <p className="text-white/60 text-lg mt-4 max-w-2xl">
          Ye form ~10 min me fill karo — aapka creator workspace, brand kit, payment setup aur pehla webinar draft, sab ek saath ready ho jaayega.
        </p>
      </section>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 pb-24 space-y-6" data-testid="onboarding-form">
        {/* SECTION 1 — Account */}
        <Section icon={Users} title="Basic account" tint="#0B5CFF">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <Input required data-testid="ob-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Kamlesh Sharma" />
            </Field>
            <Field label="Login email" required>
              <Input required type="email" data-testid="ob-email" value={form.email} onChange={set("email")} placeholder="you@brand.com" />
            </Field>
            <Field label="Set a password" required hint="Min 8 characters">
              <Input required type="password" data-testid="ob-password" value={form.password} onChange={set("password")} placeholder="••••••••" />
            </Field>
            <Field label="WhatsApp number" required hint="For attendee reminders & 2FA">
              <div className="flex gap-2">
                <Input className="w-20" value={form.country_code} onChange={set("country_code")} />
                <Input required data-testid="ob-whatsapp" className="flex-1" value={form.whatsapp} onChange={set("whatsapp")} placeholder="9876543210" />
              </div>
            </Field>
            <Field label="Timezone">
              <Input value={form.timezone} onChange={set("timezone")} placeholder="Asia/Kolkata" />
            </Field>
          </div>
        </Section>

        {/* SECTION 2 — Brand */}
        <Section icon={Building2} title="Brand & positioning" tint="#7C3AED">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business / brand name" required>
              <Input required data-testid="ob-business-name" value={form.business_name} onChange={set("business_name")} placeholder="AI Marketing Kit" />
            </Field>
            <Field label="Niche / topic">
              <Input value={form.niche} onChange={set("niche")} placeholder="AI for marketers" />
            </Field>
            <Field label="Target audience">
              <Input value={form.target_audience} onChange={set("target_audience")} placeholder="Freshers with 0-2 yr exp" />
            </Field>
            <Field label="One-line USP" hint="What you promise in 12 words">
              <Input value={form.usp_line} onChange={set("usp_line")} placeholder="Roz 1hr, 30 din me AI se agency chalao" />
            </Field>
          </div>
          <Field label="Speaker bio" hint="100–150 words — landing page pe show hoga">
            <TextArea rows={4} value={form.speaker_bio} onChange={set("speaker_bio")} placeholder="10 years of experience in..." />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Speaker photo URL" hint="Square 500×500 minimum">
              <Input value={form.speaker_photo_url} onChange={set("speaker_photo_url")} placeholder="https://…" />
            </Field>
            <Field label="Logo URL" hint="PNG transparent preferred">
              <Input value={form.logo_url} onChange={set("logo_url")} placeholder="https://…" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Primary color (hex)">
              <div className="flex gap-2 items-center">
                <input type="color" value={form.primary_color} onChange={set("primary_color")} className="h-11 w-14 rounded-lg bg-white/5 border border-white/15 cursor-pointer" />
                <Input className="flex-1" value={form.primary_color} onChange={set("primary_color")} />
              </div>
            </Field>
            <Field label="Accent color (hex)">
              <div className="flex gap-2 items-center">
                <input type="color" value={form.accent_color} onChange={set("accent_color")} className="h-11 w-14 rounded-lg bg-white/5 border border-white/15 cursor-pointer" />
                <Input className="flex-1" value={form.accent_color} onChange={set("accent_color")} />
              </div>
            </Field>
          </div>
        </Section>

        {/* SECTION 3 — Socials */}
        <Section icon={Palette} title="Social handles (optional)" tint="#EC4899">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Instagram"><Input value={form.instagram} onChange={set("instagram")} placeholder="@handle" /></Field>
            <Field label="YouTube"><Input value={form.youtube} onChange={set("youtube")} placeholder="@channel" /></Field>
            <Field label="LinkedIn"><Input value={form.linkedin} onChange={set("linkedin")} placeholder="linkedin.com/in/…" /></Field>
          </div>
        </Section>

        {/* SECTION 4 — Payment */}
        <Section icon={CreditCard} title="Payment collection (Razorpay)" tint="#22D3EE">
          <p className="text-sm text-white/50 -mt-2">Agar aap paid webinars/courses bechna chahte ho, Razorpay account details do. Free webinars ke liye skip kar sakte ho.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Razorpay KEY_ID" hint="From dashboard → Settings → API Keys">
              <Input value={form.razorpay_key_id} onChange={set("razorpay_key_id")} placeholder="rzp_live_…" />
            </Field>
            <Field label="Razorpay KEY_SECRET" hint="Same page — will be stored securely">
              <Input type="password" value={form.razorpay_key_secret} onChange={set("razorpay_key_secret")} placeholder="••••••••" />
            </Field>
            <Field label="PAN number" hint="For Razorpay KYC">
              <Input value={form.pan_number} onChange={set("pan_number")} placeholder="ABCDE1234F" />
            </Field>
            <Field label="GST number (if any)">
              <Input value={form.gst_number} onChange={set("gst_number")} placeholder="27ABCDE…" />
            </Field>
            <Field label="Business address">
              <Input value={form.business_address} onChange={set("business_address")} placeholder="City, State" />
            </Field>
            <Field label="Pincode">
              <Input value={form.business_pincode} onChange={set("business_pincode")} placeholder="110001" />
            </Field>
          </div>
        </Section>

        {/* SECTION 4.5 — Zoom (per-client) */}
        <Section icon={CreditCard} title="Zoom account (per-client)" tint="#2D8CFF">
          <p className="text-sm text-white/50 -mt-2">
            Aapke webinars aapke apne Zoom account pe run honge — aapki branding, aapka license, aapke attendees.
            Zoom Marketplace me ek Server-to-Server OAuth app banao (5 min) aur teenon values yahaan paste karo.
            Complete guide milega: <a href="/app/zoom" target="_blank" rel="noreferrer" className="text-[#22D3EE] underline">Zoom setup guide →</a>
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Zoom Account ID">
              <Input value={form.zoom_account_id} onChange={set("zoom_account_id")} placeholder="abc12XYZ_defgHI34" />
            </Field>
            <Field label="Zoom Client ID">
              <Input value={form.zoom_client_id} onChange={set("zoom_client_id")} placeholder="pKq…" />
            </Field>
            <Field label="Zoom Client Secret" hint="Shown only once in Zoom — copy carefully">
              <Input type="password" value={form.zoom_client_secret} onChange={set("zoom_client_secret")} placeholder="••••••••" />
            </Field>
          </div>
          <div className="text-xs text-white/50 border-t border-white/10 pt-3">
            <strong>Required scopes</strong> in your Zoom app: <span className="font-mono">user:read:admin</span>, <span className="font-mono">meeting:write:admin</span>, <span className="font-mono">meeting:write:registrant:admin</span>. For Zoom Webinars: add <span className="font-mono">webinar:write:admin</span> + <span className="font-mono">webinar:write:registrant:admin</span>.
          </div>
        </Section>

        {/* SECTION 5 — First webinar */}
        <Section icon={Sparkles} title="First webinar draft (optional)" tint="#F59E0B">
          <p className="text-sm text-white/50 -mt-2">Agar aapko pehla webinar aaj hi launch karna hai, uska basic detail yahaan bhar do — hum draft bana denge, aap dashboard se schedule kar dena.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Webinar title"><Input value={form.first_webinar_title} onChange={set("first_webinar_title")} placeholder="AI se agency scaling — live workshop" /></Field>
            <Field label="Price (INR)" hint="0 for free">
              <Input type="number" min={0} value={form.first_webinar_price} onChange={set("first_webinar_price")} />
            </Field>
          </div>
        </Section>

        {err && (
          <div className="rounded-xl bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 text-sm" data-testid="onboarding-error">
            {err}
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
          <div className="text-xs text-white/45 max-w-md">
            By submitting, aap Videoepreneur ki <a href="/terms" className="text-[#22D3EE] hover:underline">Terms</a> aur <a href="/privacy" className="text-[#22D3EE] hover:underline">Privacy Policy</a> se agree karte ho.
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="onboarding-submit"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-[#050B24] font-bold text-sm hover:scale-[1.03] transition-transform disabled:opacity-50"
          >
            {submitting ? "Setting up…" : "Complete setup"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
